import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, Outlet } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import AppShell from "components/layout/AppShell";
import NotFound from "pages/NotFound";
import LoginPage from './pages/login';
import PasswordResetPage from './pages/password-reset';
import JobsManagement from './pages/jobs-management';
import { auth } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import RequireRole from './lib/RequireRole';
import { useRole } from './lib/roles';
import { clearOperatorSession } from './lib/OperatorContext';
import SettingsPage from './pages/settings';
import ExchangeDeskPage from './pages/exchange-desk';
import ExchangeDeskContractPage from './pages/exchange-desk/contract';
import ProductListingPage from './pages/product-listing';
import HomePage from './pages/home';
import TimesheetReviewPage from './pages/timesheet-review';
import ArtGoldAdminPage from './pages/artgold-admin';
import TimesheetPage from './pages/timesheet';
import EmployeeLoginPage from './pages/timesheet/login';
import TimesheetLayout from './pages/timesheet/layout';
import { PORTAL_ROLES, OPERATIONS_ROLES, FINANCE_ROLES } from './lib/portalAccess.mjs';

const RequireAuth = ({ loginPath = '/login' }) => {
  const [ready, setReady] = useState(false)
  const [user, setUser] = useState(() => auth.currentUser)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => { setUser(u); setReady(true) })
    return () => unsub && unsub()
  }, [])
  if (!ready) return null
  if (!user) return <Navigate to={loginPath} replace />
  return <Outlet />
}

const LandingRedirect = () => {
  const { role, loading, user } = useRole()
  if (loading) return null
  if (!user) return <Navigate to="/login" replace />
  if (role === 'accountant') return <Navigate to="/artgold-admin" replace />
  if (PORTAL_ROLES.includes(role)) return <Navigate to="/home" replace />
  // No claim, or a retired claim (professional/finance/partner): static notice,
  // no redirects — a role-holding user must never be bounced back to "/".
  return <NoAccessNotice />
}

const NoAccessNotice = () => {
  const { user } = useRole()
  const email = user?.email || 'your account'
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card border border-border rounded-2xl p-8 text-center">
        <h1 className="text-lg font-semibold text-foreground mb-2">No portal access</h1>
        <p className="text-sm text-muted-foreground mb-6">
          The account {email} does not have access to this portal.
          If you believe you need access, contact your administrator.
        </p>
        <button
          onClick={async () => {
            clearOperatorSession()
            try { await signOut(auth) } catch {}
          }}
          className="px-4 py-2 rounded-lg bg-primary/10 border border-primary/30 text-sm font-medium text-foreground hover:bg-primary/20 transition-colors"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RouterRoutes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/password-reset" element={<PasswordResetPage />} />
        <Route path="/timesheet/login" element={<EmployeeLoginPage />} />
        <Route element={<RequireAuth loginPath="/timesheet/login" />}>
          <Route element={<RequireRole roles={PORTAL_ROLES} />}>
            <Route element={<TimesheetLayout />}>
              <Route path="/timesheet" element={<TimesheetPage />} />
              <Route path="/timesheet-review" element={<TimesheetReviewPage />} />
            </Route>
          </Route>
        </Route>
        <Route element={<RequireAuth />}>
          <Route element={<AppShell />}>
            {/* Admin Control Hub (moved into Settings) */}
            <Route element={<RequireRole roles={["admin"]} />}>
              <Route path="/control" element={<Navigate to="/settings?tab=admin-overview" replace />} />
            </Route>

            {/* Root landing: redirect based on role */}
            <Route path="/" element={<LandingRedirect />} />

            <Route element={<RequireRole roles={FINANCE_ROLES} />}><Route path="/artgold-admin" element={<ArtGoldAdminPage />} /></Route>
            <Route element={<RequireRole roles={[...PORTAL_ROLES, 'accountant']} />}><Route path="/home" element={<HomePage />} /></Route>
            {/* Portal areas: Home | Jobs | Exchange Desk | Product Listing */}
            <Route element={<RequireRole roles={PORTAL_ROLES} />}>

              <Route path="/jobs-management" element={<JobsManagement />} />
              <Route path="/jobs" element={<Navigate to="/jobs-management" replace />} />
            </Route>
            <Route element={<RequireRole roles={OPERATIONS_ROLES} />}>
              <Route path="/exchange-desk" element={<ExchangeDeskPage />} />
              <Route path="/exchange-desk/contract/:transactionId" element={<ExchangeDeskContractPage />} />
              <Route path="/product-listing" element={<ProductListingPage />} />
              {/* Preserve old bookmarks without loading the retired workspace. */}
              <Route path="/studio/*" element={<Navigate to="/product-listing" replace />} />
            </Route>

            {/* Admin-only */}
            <Route element={<RequireRole roles={["admin"]} />}>
              <Route path="/settings" element={<SettingsPage />} />
            </Route>

            {/* Authenticated Not Found */}
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
